import { formatDisplayDate } from "../utils/displayDate";
import type { ContractTemplate, Section, ContentItem } from "../types/contractTemplate";
import { mockContractTemplates } from "../data/contractTemplates";
import { ApiError, apiRequest } from "./apiClient";

type BackendContentItem = {
  readonly text?: string;
  readonly type?: string;
  readonly text_as_html?: string | null;
  readonly created_at?: string | null;
};

type BackendSection = {
  readonly sectionName?: string;
  readonly section_name?: string;
  readonly businessUnit?: string[] | null;
  readonly business_unit?: string[] | string | null;
  readonly active_version?: string | null;
  readonly section_payload?: {
    readonly business_unit?: string | null;
    readonly content?: BackendContentItem[] | null;
  } | null;
  readonly content?: BackendContentItem[] | null;
};

type BackendContractDetail = {
  readonly id: string;
  readonly name?: string | null;
  readonly contract_type?: string | null;
  readonly created_at?: string | null;
  readonly updated_at?: string | null;
  readonly payload?: {
    readonly sections?: BackendSection[] | null;
  } | null;
};

type BackendContractDetailResponse = {
  readonly data: BackendContractDetail;
};

const toContentItems = (
  section: BackendSection,
  fallbackCreatedAt?: string
): ContentItem[] => {
  const raw =
    section.content ??
    section.section_payload?.content ??
    [];

  if (!raw || raw.length === 0) {
    return [];
  }

  return raw
    .filter((item): item is BackendContentItem & { text: string } => !!item && typeof item.text === "string")
    .map((item) => ({
      type: (
        item.type === "list_item"
          ? "list_item"
          : item.type === "table"
            ? "table"
            : "clause"
      ) as ContentItem["type"],
      text: item.text,
      textAsHtml: typeof item.text_as_html === "string" ? item.text_as_html : undefined,
      createdAt: item.created_at ?? fallbackCreatedAt
    }));
};

const toBusinessUnits = (section: BackendSection): string[] => {
  if (Array.isArray(section.businessUnit) && section.businessUnit.length > 0) {
    return section.businessUnit;
  }
  if (Array.isArray(section.business_unit) && section.business_unit.length > 0) {
    return section.business_unit;
  }
  const buFromPayload = section.section_payload?.business_unit;
  if (typeof buFromPayload === "string" && buFromPayload.trim().length > 0) {
    if (buFromPayload === "On Call") {
      return ["On Call"];
    }
    return [buFromPayload];
  }
  // Default per spec: null / missing → All Bus
  return ["All Bus"];
};

const toSections = (backend: BackendContractDetail): Section[] => {
  const rawSections = backend.payload?.sections ?? [];
  const fallbackCreatedAt = backend.created_at ?? backend.updated_at ?? undefined;

  if (!Array.isArray(rawSections) || rawSections.length === 0) {
    return [];
  }

  return rawSections.map((sec, idx) => {
    const name = sec.sectionName ?? sec.section_name ?? "Section";
    const businessUnit = toBusinessUnits(sec);
    const content = toContentItems(sec, fallbackCreatedAt);

    return {
      sectionName: name,
      businessUnit,
      activeVersion: sec.active_version ?? `s${idx + 1}`,
      content: content.length > 0
        ? content
        : [
            {
              type: "clause" as const,
              text: "Section content is not available.",
              createdAt: fallbackCreatedAt
            }
          ]
    };
  });
};

export const fetchContractTemplate = async (
  contractId: string
): Promise<ContractTemplate | null> => {
  try {
    const response = await apiRequest(`/contracts/${contractId}`);
    const json = (await response.json()) as BackendContractDetailResponse;
    const backend = json.data;

    const sections = toSections(backend);
    const contractName =
      backend.name && backend.name.trim().length > 0
        ? backend.name
        : backend.contract_type ?? "Contract";

    const effectiveDateRaw = backend.created_at ?? backend.updated_at ?? null;
    const effectiveDate = effectiveDateRaw
      ? formatDisplayDate(effectiveDateRaw)
      : formatDisplayDate(new Date());

    const rawSections = sections.length > 0 ? sections : mockContractTemplates["cd-001"]?.sections ?? [];
    const enriched = rawSections.map((sec, idx) => ({
      ...sec,
      activeVersion: sec.activeVersion ?? `s${idx + 1}`,
      content: sec.content.map((item) => ({
        ...item,
        createdAt: item.createdAt ?? effectiveDate ?? "—"
      }))
    }));

    const template: ContractTemplate = {
      contractName,
      contractType: backend.contract_type ?? "Contract",
      contractReviewer: "",
      contractOrigination: "",
      effectiveDate,
      sections: enriched
    };

    return template;
  } catch {
    // Fallback to mock templates if backend is unavailable or payload is incompatible
    const fallback = mockContractTemplates[contractId] ?? mockContractTemplates["cd-001"];
    if (fallback) {
      return enrichMockWithVersioning(fallback);
    }
    return null;
  }
};

/** Enrich mock sections with activeVersion and createdAt; used when backend returns mock data */
function enrichMockWithVersioning(t: ContractTemplate): ContractTemplate {
  return {
    ...t,
    sections: t.sections.map((sec, idx) => ({
      ...sec,
      activeVersion: sec.activeVersion ?? `s${idx + 1}`,
      content: sec.content.map((item) => ({
        ...item,
        createdAt: item.createdAt ?? t.effectiveDate ?? "—"
      }))
    }))
  };
}

// ---- Section Approval (017-section-approval-s3) ----

export type ApproveSectionRequest = {
  readonly section_payload: Record<string, unknown>;
  readonly business_units: string[];
  readonly content_risk_level:
    | "ExecDecision"
    | "BusinessDecision"
    | "LegalDecision";
};

export type ApprovedSectionData = {
  readonly id: string;
  readonly contract_id: string;
  readonly source_section_id: string;
  readonly section_name: string;
  readonly normalized_section_name: string;
  readonly normalized_contract_type: string;
  readonly business_units: string[];
  readonly section_payload: Record<string, unknown>;
  readonly version_number: number;
  readonly global_version_number: number;
  readonly is_active: boolean;
  readonly approved_by: string;
  readonly content_risk_level: string;
  readonly s3_paths: string[] | null;
  readonly sort_order: number;
  readonly created_at: string;
};

type ApprovedSectionResponse = {
  readonly success: boolean;
  readonly data: ApprovedSectionData;
  readonly message: string;
};

export const excludeSectionFromTemplate = async (
  contractId: string,
  sectionId: string
): Promise<void> => {
  await apiRequest(`/contracts/${contractId}/sections/${sectionId}/exclude-from-template`, {
    method: "POST",
  });
};

export const approveSection = async (
  contractId: string,
  sectionId: string,
  body: ApproveSectionRequest
): Promise<ApprovedSectionData> => {
  const response = await apiRequest(`/contracts/${contractId}/sections/${sectionId}/approve`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = (await response.json()) as ApprovedSectionResponse;
  return json.data;
};

// ---- Approved Version History ----

export type ApprovedVersionItem = {
  readonly id: string;
  readonly contract_id: string;
  readonly source_section_id: string;
  readonly contract_name: string;
  readonly contract_type: string;
  readonly section_name: string;
  readonly normalized_section_name: string;
  readonly normalized_contract_type: string;
  readonly version_number: number;
  readonly global_version_number: number;
  readonly is_active: boolean;
  readonly approved_by: string;
  readonly business_units: string[];
  readonly content_risk_level: string;
  readonly created_at: string;
};

type ApprovedVersionsResponse = {
  readonly success: boolean;
  readonly data: ApprovedVersionItem[];
  readonly meta: { total: number; limit: number; offset: number };
};

export const fetchApprovedVersions = async (
  contractId: string,
  sectionId: string,
  params?: { limit?: number; offset?: number }
): Promise<{ items: ApprovedVersionItem[]; total: number }> => {
  const sp = new URLSearchParams();
  if (params?.limit != null) sp.set("limit", String(params.limit));
  if (params?.offset != null) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  const response = await apiRequest(
    `/contracts/${contractId}/sections/${sectionId}/versions${qs ? `?${qs}` : ""}`
  );
  const json = (await response.json()) as ApprovedVersionsResponse;
  return { items: json.data, total: json.meta.total };
};

export const fetchGlobalApprovedVersions = async (
  sectionName: string,
  contractType: string,
  params?: { limit?: number; offset?: number }
): Promise<{ items: ApprovedVersionItem[]; total: number }> => {
  const sp = new URLSearchParams();
  sp.set("section_name", sectionName);
  sp.set("contract_type", contractType);
  if (params?.limit != null) sp.set("limit", String(params.limit));
  if (params?.offset != null) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  const response = await apiRequest(`/sections/history?${qs}`);
  const json = (await response.json()) as ApprovedVersionsResponse;
  return { items: json.data, total: json.meta.total };
};

// ---- Approved Version Detail (full payload) ----

export const fetchApprovedVersionDetail = async (
  contractId: string,
  sectionId: string,
  versionId: string
): Promise<ApprovedSectionData> => {
  const response = await apiRequest(
    `/contracts/${contractId}/sections/${sectionId}/versions/${versionId}`
  );
  const json = (await response.json()) as ApprovedSectionResponse;
  return json.data;
};

// ---- Activate Version (rollback) ----

export const activateVersion = async (
  contractId: string,
  sectionId: string,
  versionId: string
): Promise<ApprovedSectionData> => {
  const response = await apiRequest(
    `/contracts/${contractId}/sections/${sectionId}/versions/${versionId}/activate`,
    { method: "POST" }
  );
  const json = (await response.json()) as ApprovedSectionResponse;
  return json.data;
};

// ---- Deactivate Version ----

export const deactivateVersion = async (
  contractId: string,
  sectionId: string,
  versionId: string
): Promise<ApprovedSectionData> => {
  const response = await apiRequest(
    `/contracts/${contractId}/sections/${sectionId}/versions/${versionId}/deactivate`,
    { method: "POST" }
  );
  const json = (await response.json()) as ApprovedSectionResponse;
  return json.data;
};

// ---- Active Approved Version ----

export const fetchActiveApproved = async (
  contractId: string,
  sectionId: string
): Promise<ApprovedSectionData | null> => {
  try {
    const response = await apiRequest(`/contracts/${contractId}/sections/${sectionId}/approved`);
    const json = (await response.json()) as ApprovedSectionResponse;
    return json.data;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
};

// ---- Section Index (lightweight: id + section_name) ----

export type SectionIndexEntry = {
  readonly id: string;
  readonly section_name: string;
};

type SectionIndexResponse = {
  readonly success: boolean;
  readonly data: SectionIndexEntry[];
  readonly meta: { total: number; limit: number; offset: number };
};

export const fetchSectionIndex = async (
  contractId: string,
  params?: { limit?: number; offset?: number; q?: string }
): Promise<{ items: SectionIndexEntry[]; total: number }> => {
  const sp = new URLSearchParams();
  if (params?.limit != null) sp.set("limit", String(params.limit));
  if (params?.offset != null) sp.set("offset", String(params.offset));
  if (params?.q) sp.set("q", params.q);
  const qs = sp.toString();
  const response = await apiRequest(
    `/contracts/${contractId}/sections/index${qs ? `?${qs}` : ""}`
  );
  const json = (await response.json()) as SectionIndexResponse;
  return { items: json.data, total: json.meta.total };
};

// ---- Section Detail (full content for one section) ----

type BackendSectionDetail = {
  readonly id: string;
  readonly contract_id: string;
  readonly contract_name: string;
  readonly contract_type: string;
  readonly section_name: string;
  readonly business_units: string[] | null;
  readonly status: string;
  readonly approval_status: string;
  readonly section_payload: {
    readonly business_unit?: string | null;
    readonly content?: BackendContentItem[] | null;
  };
  readonly storage_path: string | null;
  readonly version_number: number;
  readonly created_at: string;
};

type SectionDetailResponse = {
  readonly success: boolean;
  readonly data: BackendSectionDetail;
};

export const fetchSectionDetail = async (
  contractId: string,
  sectionId: string
): Promise<Section> => {
  const response = await apiRequest(`/contracts/${contractId}/sections/${sectionId}`);
  const json = (await response.json()) as SectionDetailResponse;
  const sec = json.data;

  const rawContent = sec.section_payload?.content ?? [];
  const content: ContentItem[] = (rawContent || [])
    .filter((item): item is BackendContentItem & { text: string } => !!item && typeof item.text === "string")
    .map((item) => ({
      type: (
        item.type === "list_item"
          ? "list_item"
          : item.type === "table"
            ? "table"
            : "clause"
      ) as ContentItem["type"],
      text: item.text,
      textAsHtml: typeof item.text_as_html === "string" ? item.text_as_html : undefined,
      createdAt: item.created_at ?? sec.created_at
    }));

  const businessUnit: string[] = Array.isArray(sec.business_units) && sec.business_units.length > 0
    ? sec.business_units
    : sec.section_payload?.business_unit
      ? [sec.section_payload.business_unit]
      : ["All Bus"];

  return {
    sectionName: sec.section_name,
    businessUnit,
    activeVersion: `v${sec.version_number}`,
    approvalStatus: sec.approval_status,
    content: content.length > 0
      ? content
      : [{ type: "clause", text: "Section content is not available.", createdAt: sec.created_at }]
  };
};
